import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { SpotifyLogo, YoutubeLogo, TwitterLogo, InstagramLogo, TiktokLogo, GoogleLogo, Plug, Download, SealCheck } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/lib/auth';

const META = {
  twitter: { icon: TwitterLogo, color: 'text-sky-400', accent: 'border-sky-400/40 bg-sky-400/5' },
  meta: { icon: InstagramLogo, color: 'text-pink-400', accent: 'border-pink-400/40 bg-pink-400/5' },
  meta_ads: { icon: InstagramLogo, color: 'text-blue-400', accent: 'border-blue-400/40 bg-blue-400/5' },
  tiktok: { icon: TiktokLogo, color: 'text-[#ccff00]', accent: 'border-[#ccff00]/40 bg-[#ccff00]/5' },
  tiktok_ads: { icon: TiktokLogo, color: 'text-[#ccff00]', accent: 'border-[#ccff00]/40 bg-[#ccff00]/5' },
  google: { icon: YoutubeLogo, color: 'text-red-500', accent: 'border-red-500/40 bg-red-500/5' },
  google_ads: { icon: GoogleLogo, color: 'text-blue-400', accent: 'border-blue-400/40 bg-blue-400/5' },
};

export default function Connections() {
  const [providers, setProviders] = useState([]);
  const [importing, setImporting] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [showPlaylists, setShowPlaylists] = useState(false);
  const [loadingPlaylists, setLoadingPlaylists] = useState(false);
  const [spotifySearchQuery, setSpotifySearchQuery] = useState('');
  const [spotifySearchResults, setSpotifySearchResults] = useState([]);
  const [searchingSpotifyPlaylists, setSearchingSpotifyPlaylists] = useState(false);
  const [spotifyPlaylistSearchMode, setSpotifyPlaylistSearchMode] = useState(false);
  const [busy, setBusy] = useState('');

  const [youtubePlaylists, setYoutubePlaylists] = useState([]);
  const [showYoutubePlaylists, setShowYoutubePlaylists] = useState(false);
  const [loadingYoutubePlaylists, setLoadingYoutubePlaylists] = useState(false);
  const [importingYoutube, setImportingYoutube] = useState(false);

  const { user, refresh } = useAuth();

  const load = () => api.get('/social/providers').then(r => setProviders(r.data.providers));
  useEffect(() => { load(); }, []);

  const youtubeProvider = providers.find(p => p.id === 'google');
  const youtubeConnected = !!youtubeProvider?.connected;

  const connectYouTube = async () => {
    setBusy('google');
    try {
      const { data } = await api.get('/social/google/authorize');
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start YouTube authorization');
      setBusy('');
    }
  };

  const loadYoutubePlaylists = async () => {
    setLoadingYoutubePlaylists(true);

    try {
      const { data } = await api.get('/social/youtube/playlists');
      setYoutubePlaylists(data.playlists || []);
      setShowYoutubePlaylists(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load YouTube playlists');
    } finally {
      setLoadingYoutubePlaylists(false);
    }
  };

  const importYoutubePlaylist = async (playlist) => {
    setImportingYoutube(true);

    try {
      await api.post(`/social/youtube/import-playlist/${playlist.id}`);
      toast.success(`Imported "${playlist.name}"`);
      setShowYoutubePlaylists(false);
      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'YouTube playlist import failed');
    } finally {
      setImportingYoutube(false);
    }
  };

  const importYoutubeVideos = async () => {
    setImportingYoutube(true);

    try {
      const { data } = await api.post('/social/youtube/import-videos');

      toast.success(
        `Imported ${data.imported} videos (${data.skipped} duplicates skipped)`
      );

      await refresh();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'YouTube video import failed');
    } finally {
      setImportingYoutube(false);
    }
  };

  const spotifyReal = user?.connections?.spotify?.mode === 'live';

  const connectSpotifyReal = async () => {
    try {
      const { data } = await api.get('/spotify/authorize');
      window.location.href = data.auth_url;
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to start Spotify auth'); }
  };

  const importCatalog = async () => {
    setImporting(true);
    try {
      const { data } = await api.post('/spotify/import');
      toast.success(`Imported ${data.imported} tracks (${data.skipped} duplicates skipped)`);
      await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); }
  };

  const loadPlaylists = async () => {
    setLoadingPlaylists(true);
    setSpotifyPlaylistSearchMode(false);
    setSpotifySearchResults([]);
    try {
      const { data } = await api.get('/spotify/playlists');
      setPlaylists(data.playlists || []);
      setShowPlaylists(true);
    } catch (e) {
      setPlaylists([]);
      setSpotifyPlaylistSearchMode(true);
      setShowPlaylists(true);
    } finally {
      setLoadingPlaylists(false);
    }
  };

  const searchSpotifyPlaylists = async () => {
    const query = spotifySearchQuery.trim();
    if (query.length < 2) {
      toast.error('Enter at least 2 characters to search Spotify playlists');
      return;
    }

    setSearchingSpotifyPlaylists(true);
    try {
      const { data } = await api.get('/spotify/search-playlists', {
        params: { q: query },
      });
      setSpotifySearchResults(data.playlists || []);
    } catch (e) {
      setSpotifySearchResults([]);
      toast.error(e.response?.data?.detail || 'Spotify playlist search failed');
    } finally {
      setSearchingSpotifyPlaylists(false);
    }
  };

  const importPlaylist = async (playlist) => {
    setImporting(true);
    try {
      const { data } = await api.post(`/spotify/import-playlist/${playlist.id}`);
      toast.success(`${playlist.name}: imported ${data.imported} tracks (${data.skipped} duplicates skipped)`);
      setShowPlaylists(false);
      await refresh();
    } catch (e) {
      const status = e.response?.status;
      const detail = e.response?.data?.detail;

      if (status === 403) {
        toast.error(
          'Spotify does not allow TuneAmplifier to read tracks from playlists owned by another user. You can import playlists you own or collaborate on.'
        );
      } else {
        toast.error(detail || 'Playlist import failed');
      }
    } finally {
      setImporting(false);
    }
  };

  const connect = async (p) => {
    setBusy(p.id);
    try {
      const { data } = await api.get(`/social/${p.id}/authorize`);
      window.location.href = data.auth_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not start authorization');
      setBusy('');
    }
  };

  const disconnect = async (p) => {
    try {
      await api.delete(`/social/${p.id}`);
      toast.success(`${p.name} disconnected`);
      await load(); await refresh();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="p-6 lg:p-8 max-w-[1400px]">
      <header className="mb-8">
        <div className="font-mono text-xs text-zinc-500 uppercase tracking-wider">Connections</div>
        <h1 className="font-display text-4xl lg:text-5xl font-bold mt-1">Plug it in.</h1>
        <p className="text-zinc-400 mt-1 text-sm max-w-2xl">Link your real accounts once — TuneAmplifier then publishes to them directly and pulls your live numbers.</p>
      </header>

      {/* Spotify */}
      <div className={`mb-4 border rounded-xl p-6 ${spotifyReal ? 'border-[#1DB954]/40 bg-[#1DB954]/5' : 'border-zinc-800 bg-zinc-900/40'}`} data-testid="spotify-real-card">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg border border-zinc-800 flex items-center justify-center">
              <SpotifyLogo size={24} weight="fill" className="text-[#1DB954]" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <div className="font-display text-lg">Spotify</div>
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${spotifyReal ? 'bg-[#1DB954]/20 text-[#1DB954]' : 'bg-zinc-800 text-zinc-500'}`}>
                  {spotifyReal ? 'Connected · LIVE' : 'Not connected'}
                </span>
              </div>
              <p className="text-sm text-zinc-400 mt-1">Real listener counts, top tracks, and bulk song import from your library.</p>
              {spotifyReal && user?.connections?.spotify?.profile?.display_name && (
                <div className="mt-2 text-xs font-mono text-zinc-500">Connected as {user.connections.spotify.profile.display_name}</div>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-2 shrink-0">
            {!spotifyReal ? (
              <Button data-testid="connect-spotify-real" onClick={connectSpotifyReal} className="btn-neon rounded-full font-display">Connect Spotify</Button>
            ) : (
  <>
    <Button
      data-testid="import-spotify-playlist"
      onClick={loadPlaylists}
      disabled={loadingPlaylists || importing}
      className="btn-neon rounded-full font-display"
    >
      <Download size={14} className="mr-1" />
      {loadingPlaylists ? 'Loading playlists…' : 'Import from Playlist'}
    </Button>

    <Button
      onClick={async () => {
        try {
          await api.post('/connections/spotify/disconnect');
          toast.success('Spotify disconnected');
          await refresh();
          window.location.reload();
        } catch (e) {
          toast.error('Disconnect failed');
        }
      }}
      className="rounded-full border border-red-500/40 bg-transparent text-red-400"
    >
      Disconnect Spotify
    </Button>
  </>
)}
          </div>
        </div>

        {showPlaylists && (
          <div className="mt-5 border border-zinc-800 rounded-xl bg-zinc-950/70 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-xs uppercase tracking-wider text-zinc-500">
                Choose Spotify Playlist
              </div>
              <button
                type="button"
                onClick={() => setShowPlaylists(false)}
                className="text-xs text-zinc-500 hover:text-zinc-200"
              >
                Close
              </button>
            </div>

            {spotifyPlaylistSearchMode ? (
              <div className="space-y-4">
                <div className="text-sm text-zinc-400">
                  Spotify did not allow TuneAmplifier to list your playlists.
                  Search public Spotify playlists instead.
                </div>

                <div className="flex gap-2">
                  <input
                    type="text"
                    value={spotifySearchQuery}
                    onChange={(e) => setSpotifySearchQuery(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') searchSpotifyPlaylists();
                    }}
                    placeholder="Search Spotify playlists…"
                    className="flex-1 min-w-0 rounded-lg border border-zinc-800 bg-zinc-900 px-3 py-2 text-sm text-zinc-100 outline-none focus:border-[#1DB954]/50"
                  />
                  <Button
                    type="button"
                    onClick={searchSpotifyPlaylists}
                    disabled={searchingSpotifyPlaylists}
                    className="rounded-lg"
                  >
                    {searchingSpotifyPlaylists ? 'Searching…' : 'Search'}
                  </Button>
                </div>

                {spotifySearchResults.length === 0 ? (
                  <div className="text-sm text-zinc-500">
                    Search for a public Spotify playlist by name.
                  </div>
                ) : (
                  <div className="space-y-2 max-h-72 overflow-y-auto">
                    {spotifySearchResults.map(playlist => (
                      <button
                        key={playlist.id}
                        type="button"
                        onClick={() => importPlaylist(playlist)}
                        disabled={importing}
                        className="w-full flex items-center justify-between gap-4 text-left border border-zinc-800 rounded-lg p-3 hover:border-[#1DB954]/50 hover:bg-[#1DB954]/5 transition-colors"
                      >
                        <div className="min-w-0">
                          <div className="text-sm text-zinc-100 truncate">
                            {playlist.name}
                          </div>
                          <div className="text-xs text-zinc-500 mt-1 truncate">
                            {playlist.owner ? `${playlist.owner} · ` : ''}
                            Public Spotify playlist
                          </div>
                        </div>
                        <span className="text-xs text-[#1DB954] shrink-0">
                          Import
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : playlists.length === 0 ? (
              <div className="text-sm text-zinc-500">
                No Spotify playlists found.
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {playlists.map(playlist => (
                  <button
                    key={playlist.id}
                    type="button"
                    onClick={() => importPlaylist(playlist)}
                    disabled={importing}
                    className="w-full flex items-center justify-between gap-4 text-left border border-zinc-800 rounded-lg p-3 hover:border-[#1DB954]/50 hover:bg-[#1DB954]/5 transition-colors"
                  >
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-100 truncate">
                        {playlist.name}
                      </div>
                      <div className="text-xs text-zinc-500 mt-1">
                        {playlist.tracks} tracks
                      </div>
                    </div>
                    <span className="text-xs text-[#1DB954] shrink-0">
                      Import
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* YouTube */}
      <div
        className={`mb-4 border rounded-xl p-6 ${
          youtubeConnected
            ? 'border-red-500/40 bg-red-500/5'
            : 'border-zinc-800 bg-zinc-900/40'
        }`}
        data-testid="youtube-real-card"
      >
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-lg border border-zinc-800 flex items-center justify-center">
              <YoutubeLogo size={24} weight="fill" className="text-red-500" />
            </div>

            <div>
              <div className="flex items-center gap-2">
                <div className="font-display text-lg">YouTube</div>

                <span
                  className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${
                    youtubeConnected
                      ? 'bg-red-500/20 text-red-400'
                      : 'bg-zinc-800 text-zinc-500'
                  }`}
                >
                  {youtubeConnected ? 'Connected · LIVE' : 'Not connected'}
                </span>
              </div>

              <p className="text-sm text-zinc-400 mt-1">
                Publish videos and import your channel videos and playlists.
              </p>

              {youtubeConnected && youtubeProvider?.account && (
                <div className="mt-2 text-xs font-mono text-zinc-500">
                  Connected as{' '}
                  {youtubeProvider.account.handle ||
                    youtubeProvider.account.name}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-2 shrink-0">
            {!youtubeConnected ? (
              <Button
                data-testid="connect-youtube"
                onClick={connectYouTube}
                disabled={busy === 'google'}
                className="btn-neon rounded-full font-display"
              >
                {busy === 'google' ? 'Connecting…' : 'Connect YouTube'}
              </Button>
            ) : (
              <>
                <Button
                  data-testid="import-youtube-playlists"
                  onClick={loadYoutubePlaylists}
                  disabled={loadingYoutubePlaylists || importingYoutube}
                  className="btn-neon rounded-full font-display"
                >
                  <Download size={14} className="mr-1" />
                  {loadingYoutubePlaylists
                    ? 'Loading playlists…'
                    : 'Import from Playlist'}
                </Button>

                <Button
                  data-testid="import-youtube-videos"
                  onClick={importYoutubeVideos}
                  disabled={importingYoutube || loadingYoutubePlaylists}
                  variant="outline"
                  className="border-zinc-800 hover:border-red-500/50 rounded-full"
                >
                  <Download size={14} className="mr-1" />
                  Import Channel Videos
                </Button>

                <Button
                  onClick={() => disconnect({ id: 'google', name: 'YouTube' })}
                  className="rounded-full border border-red-500/40 bg-transparent text-red-400"
                >
                  Disconnect YouTube
                </Button>
              </>
            )}
          </div>
        </div>

        {showYoutubePlaylists && (
          <div className="mt-5 border border-zinc-800 rounded-xl bg-zinc-950/70 p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-mono text-xs uppercase tracking-wider text-zinc-500">
                Choose YouTube Playlist
              </div>

              <button
                type="button"
                onClick={() => setShowYoutubePlaylists(false)}
                className="text-xs text-zinc-500 hover:text-zinc-200"
              >
                Close
              </button>
            </div>

            {youtubePlaylists.length === 0 ? (
              <div className="text-sm text-zinc-500">
                No YouTube playlists found on this channel.
              </div>
            ) : (
              <div className="space-y-2 max-h-72 overflow-y-auto">
                {youtubePlaylists.map(playlist => (
                  <button
                    key={playlist.id}
                    type="button"
                    onClick={() => importYoutubePlaylist(playlist)}
                    disabled={importingYoutube}
                    className="w-full flex items-center justify-between gap-4 text-left border border-zinc-800 rounded-lg p-3 hover:border-red-500/50 hover:bg-red-500/5 transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {playlist.cover_url && (
                        <img
                          src={playlist.cover_url}
                          alt=""
                          className="w-10 h-10 rounded-md object-cover border border-zinc-800"
                        />
                      )}

                      <div className="min-w-0">
                        <div className="text-sm text-zinc-100 truncate">
                          {playlist.name}
                        </div>

                        <div className="text-xs text-zinc-500 mt-1">
                          {playlist.tracks} videos
                        </div>
                      </div>
                    </div>

                    <span className="text-xs text-red-400 shrink-0">
                      Import
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-8 mb-3 font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
        Social Media Connections
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger-in">
        {providers
          .filter(p => ['twitter', 'meta', 'tiktok'].includes(p.id))
          .map(p => {
            const M = META[p.id] || {
              icon: Plug,
              color: 'text-zinc-400',
              accent: 'border-zinc-800',
            };
            const Icon = M.icon;

            return (
              <div
                key={p.id}
                data-testid={`connection-${p.id}`}
                className={`border rounded-xl p-5 card-hover ${
                  p.connected
                    ? M.accent
                    : 'border-zinc-800 bg-zinc-900/40'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg border border-zinc-800 flex items-center justify-center">
                    <Icon size={20} weight="fill" className={M.color} />
                  </div>

                  <span
                    data-testid={`status-${p.id}`}
                    className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded flex items-center gap-1 ${
                      p.connected
                        ? 'bg-[#ccff00]/20 text-[#ccff00]'
                        : p.configured
                          ? 'bg-zinc-800 text-zinc-500'
                          : 'bg-amber-500/15 text-amber-400'
                    }`}
                  >
                    {p.connected
                      ? <><SealCheck size={11} weight="fill" /> live</>
                      : p.configured
                        ? 'not connected'
                        : 'app not configured'}
                  </span>
                </div>

                <div className="font-display text-lg">{p.name}</div>
                <p className="text-sm text-zinc-400 mt-1 mb-4">{p.purpose}</p>

                {p.connected && p.account && (
                  <div className="mb-4 flex items-center gap-2 text-xs font-mono text-zinc-400 border border-zinc-800 rounded-lg p-2 bg-zinc-950/60">
                    {p.account.avatar && (
                      <img
                        src={p.account.avatar}
                        alt=""
                        className="w-6 h-6 rounded-full object-cover"
                      />
                    )}
                    <span className="truncate">
                      {p.account.handle || p.account.name}
                    </span>
                    {p.account.instagram_ready === false && (
                      <span className="text-amber-400">· IG not linked</span>
                    )}
                  </div>
                )}

                {p.connected ? (
                  <Button
                    data-testid={`disconnect-${p.id}`}
                    onClick={() => disconnect(p)}
                    className="w-full rounded-full border border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:border-red-500/60 hover:text-red-400 transition-colors"
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    data-testid={`connect-${p.id}`}
                    onClick={() => connect(p)}
                    disabled={!p.configured || busy === p.id}
                    className="w-full btn-neon rounded-full font-display"
                  >
                    {busy === p.id ? 'Redirecting…' : `Connect ${p.name}`}
                  </Button>
                )}
              </div>
            );
          })}
      </div>

      <div className="mt-10 mb-3 font-mono text-[10px] uppercase text-zinc-500 tracking-wider">
        Ads Connections
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger-in">
        {[
          providers.find(p => p.id === 'meta_ads'),
          providers.find(p => p.id === 'google_ads'),
          providers.find(p => p.id === 'tiktok_ads'),
        ]
          .filter(Boolean)
          .map(p => {
            const M = META[p.id] || {
              icon: Plug,
              color: 'text-zinc-400',
              accent: 'border-zinc-800',
            };
            const Icon = M.icon;

            return (
              <div
                key={p.id}
                data-testid={`connection-${p.id}`}
                className={`border rounded-xl p-5 card-hover ${
                  p.connected
                    ? M.accent
                    : 'border-zinc-800 bg-zinc-900/40'
                }`}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="w-10 h-10 rounded-lg border border-zinc-800 flex items-center justify-center">
                    <Icon size={20} weight="fill" className={M.color} />
                  </div>

                  <span
                    data-testid={`status-${p.id}`}
                    className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${
                      p.connected
                        ? 'bg-[#ccff00]/20 text-[#ccff00]'
                        : p.configured
                          ? 'bg-zinc-800 text-zinc-500'
                          : 'bg-amber-500/15 text-amber-400'
                    }`}
                  >
                    {p.connected
                      ? 'live'
                      : p.configured
                        ? 'not connected'
                        : 'app not configured'}
                  </span>
                </div>

                <div className="font-display text-lg">{p.name}</div>
                <p className="text-sm text-zinc-400 mt-1 mb-4">{p.purpose}</p>



                {p.connected ? (
                  <Button
                    data-testid={`disconnect-${p.id}`}
                    onClick={() => disconnect(p)}
                    className="w-full rounded-full border border-zinc-700 bg-transparent text-zinc-100 hover:bg-zinc-900 hover:border-red-500/60 hover:text-red-400 transition-colors"
                  >
                    Disconnect
                  </Button>
                ) : (
                  <Button
                    data-testid={`connect-${p.id}`}
                    onClick={() => connect(p)}
                    disabled={!p.configured || busy === p.id}
                    className="w-full btn-neon rounded-full font-display"
                  >
                    {busy === p.id ? 'Redirecting…' : `Connect ${p.name}`}
                  </Button>
                )}
              </div>
            );
          })}

        <div
          data-testid="connection-x-ads"
          className="border border-zinc-800 rounded-xl p-5 bg-zinc-900/40"
        >
          <div className="flex items-start justify-between mb-4">
            <div className="w-10 h-10 rounded-lg border border-zinc-800 flex items-center justify-center">
              <TwitterLogo size={20} weight="fill" className="text-zinc-200" />
            </div>
            <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-amber-500/15 text-amber-400">
              pending
            </span>
          </div>

          <div className="font-display text-lg">X Ads</div>
          <p className="text-sm text-zinc-400 mt-1 mb-4">
            Create and manage advertising campaigns on X
          </p>

          <div className="text-xs font-mono text-amber-400 border border-amber-500/20 rounded-lg p-3 bg-amber-500/5">
            X Ads API access is pending approval.
          </div>
        </div>
      </div>

      <div className="mt-6 text-xs font-mono text-zinc-500 border border-zinc-800 rounded-lg p-4 bg-zinc-900/30 space-y-1">
        <div>· Instagram publishing needs an Instagram <span className="text-zinc-300">Business or Creator</span> account linked to your Facebook Page.</div>
        <div>· TikTok publishing stays <span className="text-zinc-300">private</span> until TikTok audits the TuneAmplifier app — that's their rule for new apps.</div>
        <div>· TikTok Ads and X Ads are shown as pending until their advertising API access is approved and integrated.</div>
        <div>· Tokens are stored server-side only and never sent to the browser.</div>
      </div>
    </div>
  );
}
